import React, { memo, useCallback, useEffect } from 'react';

// bonnet
import { useDispatch, useSelector } from 'react-redux';

import dynamic from 'next/dynamic';

import clsx from 'clsx';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';

import { useDevice } from '@bonnet/core/device';

import { useFeatures } from '@atc/features';

import {
    Col,
    Row,
    StickyElement,
    StickyTrigger,
    Toggle,
} from 'reaxl';
import { sendClick } from 'reaxl-analytics';
import { compareToggleClick } from 'reaxl-analytics-handlers';

import { getCompareListingsIds } from '@/utilities/compareListingsHandler';
import saveListingAction from '@/utilities/saveListing';
import sendAlertClickHandler from '@/utilities/sendAlertClickHandler';

import compareListingsDuck from '@/ducks/compareListingsDuck.js';
import userPreferencesDuck from '@/ducks/userPreferencesDuck.js';

import useSrpNavigation from '@/hooks/useSrpNavigation';

// containers
import HeaderContentContainer from '@/containers/HeaderContentContainer';
import KeywordSearchContainer from '@/containers/KeywordSearchContainer';
import { KeywordSearchTrigger } from '@/containers/KeywordSearchModalContainer';

import LazyComponent from '@/components/LazyComponent';

import FilterMobileControlsContainer from './FilterMobileControlsContainer';

const CompareListingsDrawer = dynamic(() => import(
    /* webpackChunkName: "CompareListingsDrawer" */
    '@/components/CompareListingsDrawer'
), { ssr: false });

function SRPHeaderContainer({
    currentUrl,
}) {
    const navigateToSrp = useSrpNavigation();
    const dispatch = useDispatch();

    const saveListing = useCallback((state) => dispatch(saveListingAction(state)), [dispatch]);
    const compareToggle = useSelector(userPreferencesDuck.selectors.getCompareToggle);

    const {
        advanced_keyword_search: [enableAdvancedKeywordSearch],
        compare: [enableCompare, { showSpotlight, showHeaderSticky }],
        suggested_keyword: [enableKeyword],
        srp_header_content: [showHeaderContent],
        srp_redesigned_sticky_header: [redesignedStickyHeader],
    } = useFeatures([
        'advanced_keyword_search',
        'compare',
        'srp_header_content',
        'suggested_keyword',
        'srp_redesigned_sticky_header',
    ]);

    const device = useDevice();
    const isXs = _get(device, 'is.xs', false);
    const isLg = _get(device, 'is.lg', false);

    const initializeCompareListings = async () => {
        if (getCompareListingsIds()?.length > 0) {
            dispatch(userPreferencesDuck.creators.setKeys({ showDrawer: true }));

            // moved from CompareListingsDrawer, avoid calling the listing every time the first vehicle is added to compare
            // only call when it have the initial compared listing.
            dispatch(compareListingsDuck.creators.loadListingsData(getCompareListingsIds(), 5, showSpotlight));
        }
    };

    useEffect(() => {
        if (enableCompare && compareToggle) {
            initializeCompareListings();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSignIn = useCallback(() => {
        // push new instance of srp to history
        // need to call this to get save search...
        // TODO: BONNET - I assume that this is actually updating the url?
        // If if isn't then we need a better way to hydrate the page
        navigateToSrp({ resetPagination: true });
    }, [navigateToSrp]);

    const toggleCompare = useCallback((event) => {
        if (compareToggle) {
            dispatch(userPreferencesDuck.creators.setKeys({
                showDrawer: false,
            }));
            dispatch(userPreferencesDuck.creators.setCompareToggle(false));
        } else {
            sendClick(compareToggleClick, event);
            dispatch(userPreferencesDuck.creators.setCompareToggle(true));
            if (getCompareListingsIds()?.length > 0) {
                dispatch(userPreferencesDuck.creators.setKeys({ showDrawer: true }));

                // moved from CompareListingsDrawer, avoid calling the listing every time the first vehicle is added to compare
                // only call when it have the initial compared listing.
                dispatch(compareListingsDuck.creators.loadListingsData(getCompareListingsIds(), 5, showSpotlight));
            }
        }
    }, [compareToggle, dispatch, showSpotlight]);

    const showStickyHeader = (enableKeyword && (isXs || enableCompare)) || showHeaderSticky;
    const ariaLabel = compareToggle ? 'Disable' : 'Enable';
    const renderCompareToggle = (
        <label
            className="display-flex align-items-center margin-bottom-0"
            htmlFor="sticky-header-compare-toggle"
        >
            <span
                className="margin-right-2 text-gray-darker text-normal cursor-pointer"
            >
                Compare
            </span>
            <Toggle
                id="sticky-header-compare-toggle"
                checked={compareToggle}
                className="display-flex align-self-center"
                onChange={toggleCompare}
                ariaLabel={`${ariaLabel} compare vehicles`}
                data-cmp="toggle-compare"
            />
        </label>
    );

    return (
        <>
            <HeaderContentContainer
                onLogInSuccess={handleSignIn}
                showHeaderContent={showHeaderContent}
                saveListing={saveListing}
                sendAlertClickHandler={useCallback(sendAlertClickHandler, [])}
            />
            {/* Display the sticky header with a gray background on desktop only when the compare drawer is open */}
            {showStickyHeader && (
                <>
                    <StickyTrigger
                        showOnLeave="top"
                        hideOnEnter="top"
                    />
                    <StickyElement
                        id="sticky-header-srp"
                        position="top"
                    >
                        <div className="container-fluid padding-0 bg-neutral-primary display-sm-flex justify-content-sm-center">
                            <div className="container padding-0 padding-horizontal-sm-2">
                                <Row>
                                    {(!redesignedStickyHeader || (redesignedStickyHeader && isLg)) && (
                                        <Col
                                            xs={12}
                                            sm={6}
                                            md={redesignedStickyHeader ? 3 : 8}
                                            className="padding-vertical-2"
                                        >
                                            <Row className="display-flex align-items-center justify-content-center">
                                                <Col
                                                    xs={8}
                                                    sm={12}
                                                    className="margin-left-2 margin-sm-0"
                                                >
                                                    {enableAdvancedKeywordSearch ? <KeywordSearchTrigger /> : <KeywordSearchContainer isStickyHeader />}
                                                </Col>
                                                {isXs && (
                                                    <Col
                                                        xs={4}
                                                    >
                                                        {renderCompareToggle}
                                                    </Col>
                                                )}
                                            </Row>
                                        </Col>
                                    )}
                                    <Col
                                        xs={12}
                                        lg={redesignedStickyHeader ? 9 : 4}
                                        className={clsx({ 'padding-vertical-2': redesignedStickyHeader })}
                                    >
                                        <FilterMobileControlsContainer
                                            renderCompareToggle={renderCompareToggle}
                                            isStickyHeader={showStickyHeader}
                                        />
                                    </Col>
                                </Row>
                            </div>
                        </div>
                        {compareToggle
                            && (
                                <div className="container-fluid padding-0 bg-gray-darker">
                                    <LazyComponent>
                                        <CompareListingsDrawer
                                            isStickyHeader={showStickyHeader}
                                            isCollapsible={isXs}
                                            parent={isXs ? 'mobile-filter' : 'sticky'}
                                        />
                                    </LazyComponent>
                                </div>
                            )}
                    </StickyElement>
                </>
            )}
        </>
    );
}

export default memo(SRPHeaderContainer, _isEqual);
